/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jun 26 2024
 **/

import { useAsyncCallback } from "react-async-hook";

import MeetingService from "./api";

export default function withMeeting() {
	const meetingAPI = {
		conferences: {
			list: useAsyncCallback(MeetingService.conference.list),
			create: useAsyncCallback(MeetingService.conference.create),
			participateInfo: useAsyncCallback(MeetingService.conference.participateInfo),
			participate: useAsyncCallback(MeetingService.conference.participate),
			participateOk: useAsyncCallback(MeetingService.conference.participateOk),
			leave: useAsyncCallback(MeetingService.conference.leave),
			update: useAsyncCallback(MeetingService.conference.update),
			toPublic: useAsyncCallback(MeetingService.conference.toPublic),
			toPrivate: useAsyncCallback(MeetingService.conference.toPrivate),
			detail: useAsyncCallback(MeetingService.conference.detail),
			partipateFormSchedule: useAsyncCallback(MeetingService.conference.participateFromSchedule)
		},
		actionLog: {
			list: useAsyncCallback(MeetingService.actionLog.list),
			save: useAsyncCallback(MeetingService.actionLog.save)
		},
		group: {
			list: useAsyncCallback(MeetingService.group.list)
		},
		meeting: {
			list: useAsyncCallback(MeetingService.meeting.list),
			detail: useAsyncCallback(MeetingService.meeting.detail),
			remove: useAsyncCallback(MeetingService.meeting.remove),
			simple: useAsyncCallback(MeetingService.meeting.simple),
			existingDates: useAsyncCallback(MeetingService.meeting.existingDates)
		},
		schedule: {
			create: useAsyncCallback(MeetingService.schedule.create),
			unknownInvitation: useAsyncCallback(MeetingService.schedule.unknownInvitation),
			denyInvitation: useAsyncCallback(MeetingService.schedule.denyInvitation),
			acceptInvitation: useAsyncCallback(MeetingService.schedule.acceptInvitation),
			update: useAsyncCallback(MeetingService.schedule.update),
			saveInvitationReaction: useAsyncCallback(MeetingService.schedule.saveInvitationReaction)
		},
		confernceHistory: {
			downloadChat: useAsyncCallback(MeetingService.conferenceHistory.downloadChat),
			downloadActionLog: useAsyncCallback(MeetingService.conferenceHistory.downloadActionLog)
		},
		confernceInvite: {
			invite: useAsyncCallback(MeetingService.conferenceInvite.invite)
		},
		guest: {
			create: useAsyncCallback(MeetingService.meeting.guest)
		}
	};

	return {
		conference: {
			async list(options?: Meeting.Conference.List.Request) {
				return (await meetingAPI.conferences.list.execute(options)).data;
			},
			async create(options: Meeting.Conference.Create.Request) {
				return (await meetingAPI.conferences.create.execute(options)).data;
			},
			async participateInfo(options: Meeting.Conference.ParticipateInfo.Request) {
				return (await meetingAPI.conferences.participateInfo.execute(options)).data;
			},
			async participate(options: Meeting.Conference.Participate.Request) {
				return (await meetingAPI.conferences.participate.execute(options)).data;
			},
			async participateOk(options: Meeting.Conference.ParticipateOk.Request) {
				return (await meetingAPI.conferences.participateOk.execute(options)).data;
			},
			async leave(options: Meeting.Conference.Leave.Request) {
				return (await meetingAPI.conferences.leave.execute(options)).data;
			},
			async update(options: Meeting.Conference.Update.Request) {
				return (await meetingAPI.conferences.update.execute(options)).data;
			},
			async toPublic(options: Meeting.Conference.ToPublic.Request) {
				return (await meetingAPI.conferences.toPublic.execute(options)).data;
			},
			async toPrivate(options: Meeting.Conference.ToPrivate.Request) {
				return (await meetingAPI.conferences.toPrivate.execute(options)).data;
			},
			async detail(options: Meeting.Conference.Detail.Request) {
				return (await meetingAPI.conferences.detail.execute(options)).data;
			},
			async partipateFormSchedule(options: Meeting.Conference.ParticipateFromSchedule.Request) {
				return (await meetingAPI.conferences.partipateFormSchedule.execute(options)).data;
			}
		},
		actionLog: {
			async list(options?: Meeting.ActionLog.List.Request) {
				return (await meetingAPI.actionLog.list.execute(options)).data;
			},
			async save(options: Meeting.ActionLog.Save.Request) {
				return (await meetingAPI.actionLog.save.execute(options)).data;
			}
		},
		group: {
			async list(options?: Meeting.Group.List.Request) {
				return (await meetingAPI.group.list.execute(options)).data;
			}
		},
		meeting: {
			async list(options?: Meeting.Meeting.List.Request) {
				return (await meetingAPI.meeting.list.execute(options)).data;
			},
			async detail(options: Meeting.Meeting.Detail.Request) {
				return (await meetingAPI.meeting.detail.execute(options)).data;
			},
			async remove(options: Meeting.Meeting.Remove.Request) {
				return (await meetingAPI.meeting.remove.execute(options)).data;
			},
			async simple(options: Meeting.Meeting.Simple.Request) {
				return (await meetingAPI.meeting.simple.execute(options)).data;
			},
			async existingDates(options: Meeting.MeetingExistingDates.Request) {
				return (await meetingAPI.meeting.existingDates.execute(options)).data;
			}
		},

		schedule: {
			async create(options: Meeting.Schedule.Create.Request) {
				return (await meetingAPI.schedule.create.execute(options)).data;
			},
			async unknownInvitation(options: Meeting.Schedule.UnknownInvitation.Request) {
				return (await meetingAPI.schedule.unknownInvitation.execute(options)).data;
			},
			async denyInvitation(options: Meeting.Schedule.DenyInvitation.Request) {
				return (await meetingAPI.schedule.denyInvitation.execute(options)).data;
			},
			async acceptInvitation(options: Meeting.Schedule.AcceptInvitation.Request) {
				return (await meetingAPI.schedule.acceptInvitation.execute(options)).data;
			},
			async update(options: Meeting.Schedule.Update.Request) {
				return (await meetingAPI.schedule.update.execute(options)).data;
			},
			async saveReactInvitations(options: Meeting.Schedule.SaveInvitationReaction.Request) {
				return await meetingAPI.schedule.saveInvitationReaction.execute(options);
			}
		},

		conferenceHistory: {
			async downloadChat(options: Meeting.ConferenceHistory.DownloadCsvChat.Request) {
				return (await meetingAPI.confernceHistory.downloadChat.execute(options)).data;
			},
			async downloadActionLog(options: Meeting.ConferenceHistory.DownloadCsvActionLog.Request) {
				return (await meetingAPI.confernceHistory.downloadActionLog.execute(options)).data;
			}
		},
		conferenceInvite: {
			async invite(options: Meeting.ConferenceInvite.Invite.Request) {
				return (await meetingAPI.confernceInvite.invite.execute(options)).data;
			}
		},
		guest: {
			async create(options: Meeting.Guest.Request) {
				return (await meetingAPI.guest.create.execute(options)).data;
			}
		}
	};
}
